/**
 * Willtec Countdown Custom Element
 * Reads start_at & end_at ISO timestamps, updates Days, Hours, Minutes, Seconds and Milliseconds (100ms loop)
 * conditionally (only updating DOM when values change), respects Page Visibility API, and dispatches
 * 'willtec:countdown:expired' event upon completion.
 */
(() => {
  if (customElements.get('willtec-countdown')) return;

  class WilltecCountdown extends HTMLElement {
  constructor() {
    super();
    this.timer = null;
    this.lastValues = {};
  }

  connectedCallback() {
    this.endAtStr = this.getAttribute('end-at');
    this.startAtStr = this.getAttribute('start-at');

    if (!this.endAtStr) {
      this.hideAll();
      return;
    }

    this.endDate = new Date(this.endAtStr).getTime();
    if (isNaN(this.endDate)) {
      this.hideAll();
      return;
    }

    this.daysEl = this.querySelector('[data-days]');
    this.hoursEl = this.querySelector('[data-hours]');
    this.minutesEl = this.querySelector('[data-minutes]');
    this.secondsEl = this.querySelector('[data-seconds]');
    this.millisEl = this.querySelector('[data-millis]');
    this.compactEl = this.querySelector('[data-countdown-compact]');

    this.visibilityHandler = () => this.handleVisibilityChange();
    document.addEventListener('visibilitychange', this.visibilityHandler);

    this.update();
    this.start();
  }

  disconnectedCallback() {
    this.stop();
    if (this.visibilityHandler) {
      document.removeEventListener('visibilitychange', this.visibilityHandler);
    }
  }

  hideAll() {
    // Don't hide in theme editor so merchant can still configure it
    if (window.Shopify?.designMode) return;

    const section = this.closest('.willtec-flash-sale-section');
    if (section) section.style.setProperty('display', 'none', 'important');

    const pdpRow = this.closest('[data-pdp-countdown-row]');
    if (pdpRow) pdpRow.style.setProperty('display', 'none', 'important');

    this.style.setProperty('display', 'none', 'important');
  }

  start() {
    if (document.hidden) return;
    this.stop();
    const hasMillis = !!this.millisEl || !!this.compactEl;
    this.timer = setInterval(() => this.update(), hasMillis ? 50 : 1000);
  }

  stop() {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  handleVisibilityChange() {
    if (document.hidden) {
      this.stop();
    } else {
      this.update();
      this.start();
    }
  }

  update() {
    const now = Date.now();
    const diff = this.endDate - now;

    if (diff <= 0) {
      this.stop();
      this.renderZero();
      this.dispatchEvent(new CustomEvent('willtec:countdown:expired', { bubbles: true }));
      this.hideAll();
      return;
    }

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);
    const millis = Math.floor(diff % 1000);

    const formattedDays = days < 10 ? `0${days}` : `${days}`;
    const formattedHours = hours < 10 ? `0${hours}` : `${hours}`;
    const formattedMinutes = minutes < 10 ? `0${minutes}` : `${minutes}`;
    const formattedSeconds = seconds < 10 ? `0${seconds}` : `${seconds}`;
    const formattedMillis = String(millis).padStart(3, '0');

    if (this.compactEl) {
      let compactStr = '';
      if (days > 0) {
        compactStr = `${formattedDays}:${formattedHours}:${formattedMinutes}:${formattedSeconds}`;
      } else {
        const totalHours = Math.floor(diff / (1000 * 60 * 60));
        const formattedTotalHours = totalHours < 10 ? `0${totalHours}` : `${totalHours}`;
        compactStr = `${formattedTotalHours}:${formattedMinutes}:${formattedSeconds}:${formattedMillis}`;
      }
      if (this.lastValues.compact !== compactStr) {
        this.compactEl.textContent = compactStr;
        this.lastValues.compact = compactStr;
      }
    }

    if (this.daysEl && this.lastValues.days !== formattedDays) {
      this.daysEl.textContent = formattedDays;
      this.lastValues.days = formattedDays;
    }
    if (this.hoursEl && this.lastValues.hours !== formattedHours) {
      this.hoursEl.textContent = formattedHours;
      this.lastValues.hours = formattedHours;
    }
    if (this.minutesEl && this.lastValues.minutes !== formattedMinutes) {
      this.minutesEl.textContent = formattedMinutes;
      this.lastValues.minutes = formattedMinutes;
    }
    if (this.secondsEl && this.lastValues.seconds !== formattedSeconds) {
      this.secondsEl.textContent = formattedSeconds;
      this.lastValues.seconds = formattedSeconds;
    }
    if (this.millisEl && this.lastValues.millis !== formattedMillis) {
      this.millisEl.textContent = formattedMillis;
      this.lastValues.millis = formattedMillis;
    }
  }

  renderZero() {
    if (this.compactEl) this.compactEl.textContent = '00:00:00:000';
    if (this.daysEl) this.daysEl.textContent = '00';
    if (this.hoursEl) this.hoursEl.textContent = '00';
    if (this.minutesEl) this.minutesEl.textContent = '00';
    if (this.secondsEl) this.secondsEl.textContent = '00';
    if (this.millisEl) this.millisEl.textContent = '000';
  }
}

  if (!customElements.get('willtec-countdown')) {
    customElements.define('willtec-countdown', WilltecCountdown);
  }
})();
